"use client";

import { memo, ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createWalletClient, http } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { configureChains, createConfig, WagmiConfig } from "wagmi";
import { CoinbaseWalletConnector } from "wagmi/connectors/coinbaseWallet";
import { MetaMaskConnector } from "wagmi/connectors/metaMask";
import { jsonRpcProvider } from "wagmi/providers/jsonRpc";

import { RetxConnector } from "@/lib/connector";
import { Chain, Network } from "@/model";
import { networkActions } from "@/store/actions";
import { networkSelector } from "@/store/network/network.slice";

import { ClientWallet } from "@/wagmi/retx-wallet/walletClient";

export default memo(function WagmiConfigProvider({
  children,
  listNetwork,
}: {
  children: ReactNode;
  listNetwork: Network[];
}) {
  const dispatch = useDispatch();
  const { chain, countLoadWallet } = useSelector(networkSelector);
  const wallet = ClientWallet.getInstance();

  const listChains = (listNetwork) => {
    return listNetwork?.map(
      (item: Network) =>
        new Chain({
          ...item,
          chainId: item?.chainId?.toString(),
        })
    );
  };

  const { chains, publicClient, webSocketPublicClient } = configureChains(
    listChains(listNetwork),
    [
      jsonRpcProvider({
        rpc: (chain) => ({ http: chain.rpcUrls.default.http[0] }),
      }),
    ]
  );

  // get the list of networks
  useEffect(() => {
    dispatch(networkActions?.self.preGetNetworksSuccess(listNetwork));
  }, []);

  // metamask wallet
  const metaMaskConnector = new MetaMaskConnector({
    chains,
    options: {
      shimDisconnect: true,
    },
  });

  const coinbaseConnector = new CoinbaseWalletConnector({
    chains,
    options: {
      appName: "RiseGate",
      appLogoUrl: "/logo-danang.png",
    },
  });

  function createRetxWalletConnector(key: `0x${string}`, chains, chain = null) {
    const client = createWalletClient({
      account: privateKeyToAccount(key),
      chain: chain ?? chains?.[0],
      transport: http(),
    });

    const retxConnector = new RetxConnector({
      chains,
      options: {
        chainId: chain?.id ?? chains?.[0]?.id,
        walletClient: client,
        flags: {
          isAuthorized: !!key,
        },
      },
    });

    return retxConnector;
  }

  // main config
  const wagmiConfig = createConfig({
    autoConnect: true,
    connectors:
      wallet?.key && chains && countLoadWallet
        ? ([createRetxWalletConnector(wallet?.key, chains, chain)] as any[])
        : [metaMaskConnector, coinbaseConnector],
    publicClient,
    webSocketPublicClient,
  });

  return <WagmiConfig config={wagmiConfig}>{children}</WagmiConfig>;
});
